/**
 * Modale : reporter max(SO, SR) au devoir UNIQUEMENT pour ceux qui ont
 * 0 au devoir et qui ont composé l'examen.
 *
 * Garde-fous (alignés avec le backend) :
 *  - moyenne_devoirs == 0 (pas null, pas > 0)
 *  - devoir non verrouillé
 *  - SO et/ou SR avec note réelle non-absente
 *  - max(SO, SR) > 0
 *  - Toujours dry-run d'abord, puis confirmation explicite pour appliquer.
 */
import React, { useEffect, useState } from 'react';
import {
  X, Loader, AlertTriangle, CheckCircle2, ArrowRightLeft, Lock, Info,
} from 'lucide-react';
import { useReporterExamenSiDevoirZeroMutation } from '../../services/notesDevoirsApi';

interface Props {
  open: boolean;
  matiereId: number;
  matiereLabel: string;
  semestre: 'S1' | 'S2';
  anneeAcademique: string;
  classe?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const ReporterExamenSiZeroModal: React.FC<Props> = ({
  open, matiereId, matiereLabel, semestre, anneeAcademique, classe,
  onClose, onSuccess,
}) => {
  const [reporter, { isLoading }] = useReporterExamenSiDevoirZeroMutation();
  const [preview, setPreview] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<number | null>(null);
  const [confirmer, setConfirmer] = useState(false);

  // Charger le dry-run à l'ouverture
  useEffect(() => {
    if (!open) {
      setPreview(null); setError(null); setApplied(null); setConfirmer(false);
      return;
    }
    (async () => {
      try {
        const res = await reporter({
          matiere_id: matiereId,
          annee_academique: anneeAcademique,
          semestre,
          classes: classe ? [classe] : undefined,
          dry_run: true,
        }).unwrap();
        setPreview(res);
      } catch (e: any) {
        setError(e?.data?.error || e?.message || 'Erreur de prévisualisation');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAppliquer = async () => {
    setError(null);
    try {
      const res = await reporter({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        classes: classe ? [classe] : undefined,
        dry_run: false,
      }).unwrap();
      setApplied(res.applique);
      if (onSuccess) onSuccess();
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur lors de l\'application');
    }
  };

  if (!open) return null;

  const eligibles = preview?.eligibles || [];
  const ignores = preview?.ignores || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-amber-600 dark:text-amber-400" />
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                Reporter examen → devoir (uniquement si devoir = 0)
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {matiereLabel} · {semestre} · {anneeAcademique}{classe && ` · ${classe}`}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
            <X size={16} />
          </button>
        </div>

        {/* Règles */}
        <div className="px-5 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30 text-[11px] text-blue-900 dark:text-blue-200">
          <div className="flex items-start gap-2">
            <Info size={13} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold mb-0.5">Règles strictes appliquées :</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Seuls les étudiants avec <b>moyenne devoir = 0</b> sont touchés.</li>
                <li>Les devoirs <b>verrouillés</b> sont ignorés.</li>
                <li>Étudiants avec une vraie note (moyenne {'>'} 0) <b>ne sont pas touchés</b>.</li>
                <li>L'étudiant doit avoir composé <b>SO ou SR</b> (note non-absente).</li>
                <li>On reporte <b>max(SO, SR)</b> et seulement si {'>'} 0.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && !preview && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader size={28} className="animate-spin text-blue-500 mb-2" />
              <p className="text-sm text-gray-500">Analyse en cours…</p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {applied !== null && (
            <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-300 mb-3">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold">{applied} étudiant(s) mis à jour.</div>
                <div className="text-xs">Le devoir a été aligné sur max(SO, SR).</div>
              </div>
            </div>
          )}

          {preview && (
            <>
              {/* Stats résumé */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <Kpi label="Éligibles" value={preview.total_eligibles}
                  couleur="text-green-600 dark:text-green-400" />
                <Kpi label="Verrouillés" value={ignores.verrouilles}
                  couleur="text-gray-700 dark:text-gray-300" icon={<Lock size={11} />} />
                <Kpi label="Déjà notés" value={ignores.deja_note}
                  couleur="text-blue-600 dark:text-blue-400"
                  hint="moy. devoir > 0 : intacts" />
                <Kpi label="Sans examen" value={ignores.pas_examen}
                  couleur="text-gray-500" />
                <Kpi label="0 partout" value={ignores.pas_amelioration}
                  couleur="text-gray-500" hint="max(SO,SR) ≤ 0" />
              </div>

              {/* Aperçu liste */}
              {eligibles.length === 0 ? (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-10">
                  Aucun étudiant n'est éligible à ce report pour cette matière/classe.
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="px-2 py-2 text-left">Matricule</th>
                        <th className="px-2 py-2 text-left">Étudiant</th>
                        <th className="px-2 py-2 text-left">Classe</th>
                        <th className="px-2 py-2 text-right">Ancien D1/D2</th>
                        <th className="px-2 py-2 text-right">SO</th>
                        <th className="px-2 py-2 text-right">SR</th>
                        <th className="px-2 py-2 text-right">Nouvelle moy.</th>
                        <th className="px-2 py-2 text-center">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {eligibles.map((e: any) => (
                        <tr key={e.note_devoir_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-2 py-1.5 font-mono text-[11px] text-gray-700 dark:text-gray-300">{e.matricule}</td>
                          <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{e.nom}</td>
                          <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">{e.classe || '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                            {fmt(e.ancien_d1)} / {fmt(e.ancien_d2)}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmt(e.so)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmt(e.sr)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-bold text-green-600 dark:text-green-400">
                            {fmt(e.nouvelle_moyenne)}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                              e.source === 'SR'
                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            }`}>{e.source}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {preview && eligibles.length > 0 && applied === null && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={confirmer}
                onChange={(e) => setConfirmer(e.target.checked)}
                className="rounded" />
              Je confirme avoir vérifié la liste — appliquer aux <b>{eligibles.length}</b> étudiant(s)
            </label>
            <div className="flex gap-2">
              <button onClick={onClose}
                className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                Annuler
              </button>
              <button onClick={handleAppliquer}
                disabled={!confirmer || isLoading}
                className="px-3 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center gap-1.5">
                {isLoading && <Loader size={12} className="animate-spin" />}
                Appliquer le report
              </button>
            </div>
          </div>
        )}

        {applied !== null && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end">
            <button onClick={onClose}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function Kpi({ label, value, couleur, icon, hint }: {
  label: string; value: number | undefined; couleur: string;
  icon?: React.ReactNode; hint?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-center">
      <div className={`text-lg font-bold tabular-nums ${couleur} flex items-center justify-center gap-1`}>
        {icon}{value ?? 0}
      </div>
      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold mt-0.5">{label}</div>
      {hint && <div className="text-[9px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function fmt(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(2);
}

export default ReporterExamenSiZeroModal;
